import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import AppContext from "../context/AppContext";
import { useLocation, useNavigate } from "react-router-dom";
import infoIcon from "../../assets/images/info.svg";
import { toast } from "react-toastify";

import redLoc from "../../assets/images/red-loc.png";

import {
    courseList,
    getIncidentTypes,
} from "../_slice/courseSlice";

// ✅ QUEST SLICE action
import { saveCourseFixtures } from "../_slice/questSlice";
import {
    questTypeMarketplace,
    addToCart,
    removeFromCart,
} from "../_slice/questTypePackageSlice";
import QuestTypeCheckout from "./QuestTypeCheckout";
import {
    formatApiDate,
    formatFriendlyDate,
    formatPrice,
    getFreeAccessUntil,
    isFreeAccessGrant,
    isRenewable,
} from "../../utils/utils";

// Tab shown first, so no quest type can ever be hidden by grouping.
const ALL_TAB = "all";

/**
 * Whether the organisation can use a quest type, and at what price.
 * Reads the quest type itself first, then the marketplace. Returns null when
 * neither knows, in which case the row stays usable — never lock someone out
 * of a quest type on a guess.
 * @returns {{owned: boolean, isExpired: boolean, price: *}|null}
 */
const resolveOwnership = (incidentType, marketplaceEntry) => {
    if (typeof incidentType?.owned === "boolean") {
        const viaFreeAccess = isFreeAccessGrant(incidentType);
        return {
            owned: incidentType.owned,
            isExpired: Boolean(incidentType.is_expired),
            price: incidentType.price,
            expiresAt: incidentType.expires_at ?? null,
            viaFreeAccess,
            // A free quest type that is already owned is an included one.
            isDefault:
                !viaFreeAccess &&
                incidentType.owned &&
                Number(incidentType.price) === 0,
        };
    }
    if (marketplaceEntry) {
        const { questType, bundle } = marketplaceEntry;
        // Organisation Free Access: owned for now, whatever the bundle. The
        // real price is kept, it just is not payable while this lasts.
        if (isFreeAccessGrant(questType)) {
            return {
                owned: true,
                isExpired: false,
                price: questType.price ?? incidentType?.price,
                expiresAt: questType.expires_at ?? null,
                viaFreeAccess: true,
                isDefault: false,
            };
        }
        // Default bundles are always usable and never expire.
        if (bundle?.isDefault) {
            return {
                owned: true,
                isExpired: false,
                price: "0.00",
                expiresAt: null,
                isDefault: true,
            };
        }
        return {
            owned: Boolean(questType.owned),
            isExpired: Boolean(questType.is_expired),
            price: questType.price ?? incidentType?.price,
            expiresAt: questType.expires_at ?? null,
            isDefault: false,
        };
    }
    return null;
};

/**
 * Price cell for a quest type. The incidents endpoint sends `price` as a
 * decimal string; zero is shown as an amount, a missing value as a dash.
 */
const questTypePrice = (incidentType) => {
    const raw = incidentType?.price;
    if (raw === null || raw === undefined || raw === "") return "-";
    const amount = Number(raw);
    if (isNaN(amount)) return "-";
    return formatPrice(amount);
};

/**
 * Bundle/category a quest type belongs to, read from the quest type itself.
 * The incidents endpoint may or may not embed this, so every shape it could
 * reasonably take is checked before giving up.
 * @returns {{id: string|number, name: string}|null}
 */
const embeddedBundle = (incidentType) => {
    const raw =
        incidentType?.category ||
        incidentType?.incident_type_category ||
        incidentType?.bundle ||
        null;

    if (raw && typeof raw === "object" && raw.name) {
        return { id: raw.id ?? raw.name, name: raw.name };
    }
    if (typeof raw === "string" && raw.trim()) {
        return { id: raw, name: raw };
    }
    if (incidentType?.category_name) {
        return {
            id: incidentType.category_id ?? incidentType.category_name,
            name: incidentType.category_name,
        };
    }
    return null;
};

const QuestModal = ({ show, onHide, courseIncidentTypesData, courseId, mode }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { setLoader } = useContext(AppContext);
    const [showTooltip, setShowTooltip] = useState(null);

    const location = useLocation();
    const locationId = location?.state?.orglocation?.locationId || "";
    // console.log("locationId", locationId);

    // const refs = {
    //     0: { icon: useRef(null), tooltip: useRef(null) },
    //     1: { icon: useRef(null), tooltip: useRef(null) },
    //     2: { icon: useRef(null), tooltip: useRef(null) },
    //     3: { icon: useRef(null), tooltip: useRef(null) },
    //     4: { icon: useRef(null), tooltip: useRef(null) },
    // };

    // const handleTooltipToggle = (index) => {
    //     setShowTooltip(prev => (prev === index ? null : index));
    // };

    // useEffect(() => {
    //     const handleClickOutside = (event) => {
    //         if (showTooltip === null) return;
    //         const { icon, tooltip } = refs[showTooltip] || {};
    //         if (icon?.current?.contains(event.target) || tooltip?.current?.contains(event.target)) return;
    //         setShowTooltip(null);
    //     };
    //     document.addEventListener("mousedown", handleClickOutside);
    //     return () => document.removeEventListener("mousedown", handleClickOutside);
    // }, [showTooltip]);

    // state
    const [selectedIncidents, setSelectedIncidents] = useState({});
    const [selectedFixtures, setSelectedFixtures] = useState({});
    const [expandedRows, setExpandedRows] = useState({});
    // The tab the user picked, or null to fall back to the default tab.
    const [chosenTab, setChosenTab] = useState(null);

    // Bundles already loaded for the marketplace. Used only to group the quest
    // types into tabs when the incidents endpoint does not embed a category.
    const marketplaceBundles = useSelector(
        (state) => state.questTypePackage.marketplaceBundles
    );
    const organisationHasFreeAccess = useSelector(
        (state) => state.questTypePackage.organisationHasFreeAccess
    );

    // When the organisation's free access ends, from the API response.
    const freeAccessUntil = useMemo(
        () =>
            organisationHasFreeAccess ? getFreeAccessUntil(marketplaceBundles) : null,
        [organisationHasFreeAccess, marketplaceBundles]
    );

    // What the incidents endpoint returned: only the quest types this
    // organisation can actually use.
    const usableQuestTypes = useMemo(
        () => courseIncidentTypesData || [],
        [courseIncidentTypesData]
    );

    // True when the incidents endpoint already tells us the category, in which
    // case no extra request is needed.
    const hasEmbeddedBundles = useMemo(
        () => usableQuestTypes.some((incidentType) => embeddedBundle(incidentType)),
        [usableQuestTypes]
    );

    // Same question for ownership, which decides whether a quest type is
    // usable or has to be bought first.
    const hasEmbeddedOwnership = useMemo(
        () => usableQuestTypes.some((it) => typeof it?.owned === "boolean"),
        [usableQuestTypes]
    );

    // Quest type id -> { questType, bundle } straight from the marketplace.
    const marketplaceIndex = useMemo(() => {
        const index = new Map();
        (marketplaceBundles || []).forEach((bundle) => {
            (bundle.quest_types || []).forEach((questType) => {
                if (!index.has(questType.id)) index.set(questType.id, { questType, bundle });
            });
        });
        return index;
    }, [marketplaceBundles]);

    // Fall back to the marketplace for the grouping, but only when the modal is
    // open, the categories are not embedded, and nothing is cached yet.
    // The ref caps this at one request per opening: the reducer hands back a
    // fresh array each time, so an empty result would otherwise re-trigger the
    // effect and loop.
    const marketplaceRequestedRef = useRef(false);
    useEffect(() => {
        if (!show) {
            marketplaceRequestedRef.current = false;
            return;
        }
        if (hasEmbeddedBundles && hasEmbeddedOwnership) return;
        if (marketplaceBundles?.length) return;
        if (marketplaceRequestedRef.current) return;
        marketplaceRequestedRef.current = true;
        dispatch(questTypeMarketplace({ navigate, setLoader }));
    }, [
        show,
        hasEmbeddedBundles,
        hasEmbeddedOwnership,
        marketplaceBundles,
        dispatch,
        navigate,
        setLoader,
    ]);

    // Quest type id -> bundle, built from whichever source is available.
    // The incidents endpoint omits anything not yet bought, so those quest
    // types would never appear here. Fold them in from the marketplace as
    // listing-only rows: no fixtures or prompts to configure, but visible,
    // grouped under their bundle, and purchasable.
    const questTypes = useMemo(() => {
        const seen = new Set(usableQuestTypes.map((it) => it.id));
        const extras = [];
        (marketplaceBundles || []).forEach((bundle) => {
            (bundle.quest_types || []).forEach((questType) => {
                if (seen.has(questType.id)) return;
                seen.add(questType.id);
                extras.push({
                    id: questType.id,
                    name: questType.name,
                    image: questType.image,
                    price: questType.price,
                    fixtures: [],
                    incidents: [],
                    owned: Boolean(questType.owned),
                    acquired_via: questType.acquired_via ?? null,
                    is_expired: Boolean(questType.is_expired),
                    expires_at: questType.expires_at ?? null,
                });
            });
        });
        return extras.length ? [...usableQuestTypes, ...extras] : usableQuestTypes;
    }, [usableQuestTypes, marketplaceBundles]);

    const bundleByQuestTypeId = useMemo(() => {
        const map = new Map();
        questTypes.forEach((incidentType) => {
            const bundle = embeddedBundle(incidentType);
            if (bundle) map.set(incidentType.id, bundle);
        });
        if (map.size) return map;

        (marketplaceBundles || []).forEach((bundle) => {
            (bundle.quest_types || []).forEach((questType) => {
                if (!map.has(questType.id)) {
                    map.set(questType.id, {
                        id: bundle.id,
                        name: bundle.name,
                        isDefault: Boolean(bundle.isDefault),
                    });
                }
            });
        });
        return map;
    }, [questTypes, marketplaceBundles]);

    // Tabs, in the order their first quest type appears.
    const tabs = useMemo(() => {
        const seen = new Map();
        questTypes.forEach((incidentType) => {
            const bundle = bundleByQuestTypeId.get(incidentType.id);
            if (bundle && !seen.has(String(bundle.id))) {
                seen.set(String(bundle.id), bundle);
            }
        });
        return [...seen.values()];
    }, [questTypes, bundleByQuestTypeId]);

    // Opens on the Basic (default) bundle. Derived rather than set on open,
    // because the bundles arrive after the modal does. Preference: the bundle
    // flagged default, then one named "Basic", then the first; All only when
    // there are no bundle tabs at all.
    const defaultTabId = useMemo(() => {
        if (!tabs.length) return ALL_TAB;
        const preferred =
            tabs.find((tab) => tab.isDefault) ||
            tabs.find((tab) => String(tab.name).trim().toLowerCase() === "basic") ||
            tabs[0];
        return String(preferred.id);
    }, [tabs]);
    const activeTab = chosenTab ?? defaultTabId;
    const setActiveTab = setChosenTab;

    const cart = useSelector((state) => state.questTypePackage.cart);
    const isInCart = (id) => cart.some((item) => item.id === id);

    // Newly purchased quest types have to become selectable straight away.
    const handlePurchased = () => {
        if (courseId) dispatch(getIncidentTypes({ courseId, navigate, setLoader }));
        dispatch(questTypeMarketplace({ navigate, setLoader }));
    };

    const handleAddToCart = (incidentType, ownership) => {
        const entry = marketplaceIndex.get(incidentType.id);
        dispatch(
            addToCart({
                id: incidentType.id,
                name: incidentType.name,
                price: ownership?.price ?? incidentType.price,
                image: incidentType.image,
                bundleId: entry?.bundle?.id,
                bundleName: entry?.bundle?.name,
            })
        );
    };

    // Expiry line under the action, so a purchased quest type says when it runs
    // out. Always backend supplied, never calculated here.
    const renderExpiry = (ownership) => {
        if (!ownership?.expiresAt) return null;
        return (
            <div className="quest_action_cell_expiry">
                {ownership.isExpired ? "Expired on " : "Expires: "}
                {formatFriendlyDate(ownership.expiresAt)}
            </div>
        );
    };

    // Renew / Add to Cart cell, mirroring the Quest Types marketplace screen.
    const renderPurchaseAction = (incidentType, ownership) => {
        // Ownership unknown — offer nothing rather than guess.
        if (!ownership) return null;

        // Granted by Organisation Free Access: nothing to buy while it lasts.
        if (ownership.viaFreeAccess) {
            return (
                <>
                    <span className="quest_action_cell_included">Free access</span>
                    {ownership.expiresAt && (
                        <div className="quest_action_cell_expiry">
                            Included until {formatApiDate(ownership.expiresAt)}
                        </div>
                    )}
                </>
            );
        }

        // Included quest types are free and never expire, so nothing to buy.
        if (ownership.isDefault) {
            return <span className="quest_action_cell_included">Included</span>;
        }

        if (isInCart(incidentType.id)) {
            return (
                <>
                    <div className="quest_action_cell_inner">
                        <span className="quest_action_cell_incart">In Cart</span>
                        <button
                            type="button"
                            className="action_btns"
                            onClick={() => dispatch(removeFromCart(incidentType.id))}
                        >
                            Remove
                        </button>
                    </div>
                    {renderExpiry(ownership)}
                </>
            );
        }

        // Owned and not near expiry: already paid for, nothing to do.
        if (
            ownership.owned &&
            !ownership.isExpired &&
            !isRenewable(ownership.expiresAt)
        ) {
            return (
                <>
                    <button
                        type="button"
                        className="submit_btn quest_action_cell_buy"
                        style={{ opacity: 0.6 }}
                        disabled
                    >
                        Purchased
                    </button>
                    {renderExpiry(ownership)}
                </>
            );
        }

        // Owned but close to expiry, or lapsed, both renew.
        const label = ownership.owned || ownership.isExpired ? "Renew" : "Add to Cart";

        return (
            <>
                <button
                    type="button"
                    className="submit_btn quest_action_cell_buy"
                    onClick={() => handleAddToCart(incidentType, ownership)}
                >
                    {label}
                </button>
                {renderExpiry(ownership)}
            </>
        );
    };

    const tabIdFor = (incidentTypeId) => {
        const bundle = bundleByQuestTypeId.get(incidentTypeId);
        return bundle ? String(bundle.id) : null;
    };

    const visibleQuestTypes = useMemo(() => {
        if (activeTab === ALL_TAB) return questTypes;
        return questTypes.filter(
            (incidentType) => tabIdFor(incidentType.id) === activeTab
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questTypes, activeTab, bundleByQuestTypeId]);

    // 🚀 Toggle incidents (Prompts)
    const handleIncidentToggle = (incidentTypeId, incidentId) => {
        setSelectedIncidents(prev => {
            const current = prev[incidentTypeId] || [];
            if (current.includes(incidentId)) {
                return { ...prev, [incidentTypeId]: current.filter(id => id !== incidentId) };
            }
            if (current.length >= 3) return prev;
            return { ...prev, [incidentTypeId]: [...current, incidentId] };
        });
    };

    // 🚀 Toggle fixtures (Points of Interest) - nested
    const handleFixtureToggle = (incidentTypeId, fixtureId) => {
        setSelectedFixtures(prev => {
            const current = prev[incidentTypeId] || {};
            return {
                ...prev,
                [incidentTypeId]: {
                    ...current,
                    [fixtureId]: !current[fixtureId],
                }
            };
        });
    };

    const handleExpandToggle = (incidentTypeId) => {
        setExpandedRows(prev => {
            const willBeExpanded = !prev[incidentTypeId];

            // When enabling a row, auto-select its first Prompt if none chosen yet
            if (willBeExpanded) {
                const incidentType = courseIncidentTypesData?.find(
                    (it) => it.id === incidentTypeId
                );
                const firstIncidentId = incidentType?.incidents?.[0]?.id;
                if (firstIncidentId) {
                    setSelectedIncidents(prevSel => {
                        const current = prevSel[incidentTypeId] || [];
                        if (current.length > 0) return prevSel;
                        return { ...prevSel, [incidentTypeId]: [firstIncidentId] };
                    });
                }
            }

            return {
                ...prev,
                [incidentTypeId]: willBeExpanded,
            };
        });
    };

    // ✅ SUBMIT (FINAL TRUTH)
    // const onSubmit = (e) => {
    //     e.preventDefault();

    //     const incident_types = Object.keys(selectedIncidents).map((incidentTypeId) => ({
    //         incident_type_id: Number(incidentTypeId),
    //         incident_ids: selectedIncidents[incidentTypeId],
    //     }));

    //     const allFixtures = courseIncidentTypesData.flatMap(t => t.fixtures);

    //     const fixtures = Object.keys(selectedFixtures)
    //         .filter(fid => selectedFixtures[fid])
    //         .map(fid => {
    //             const fixture = allFixtures.find(f => f.id === Number(fid));

    //             return {
    //                 fixture_id: fixture.id,
    //                 reference_name: fixture.reference_name || fixture.name,
    //                 serial_number: fixture.serial_number || ""
    //             };
    //         });



    //     const payload = {
    //         course_id: courseId,
    //         incident_types,
    //         fixtures
    //     };

    //     console.log("Final API Payload", payload);

    //     dispatch(
    //         saveCourseFixtures({
    //             payload,
    //             navigate,
    //             setLoader,
    //         })
    //     ).then(() => onHide());
    // };

    // ✅ SUBMIT with validation
    const onSubmit = (e) => {
        e.preventDefault();

        // 🔎 Validation: at least 1 prompt if user interacted
        for (const incidentType of courseIncidentTypesData) {
            const selectedPromptCount =
                selectedIncidents[incidentType.id]?.length || 0;

            if (expandedRows[incidentType.id] && selectedPromptCount === 0) {
                // The offending row may sit on another tab, so switch to it.
                const tabId = tabIdFor(incidentType.id);
                setActiveTab(tabId || ALL_TAB);
                toast.error(
                    `Please select at least one Prompt for "${incidentType.name}"`
                );
                return;
            }
        }

        // ✅ INCIDENT TYPES ONLY
        // const incident_types = Object.keys(selectedIncidents).map(
        //     (incidentTypeId) => ({
        //         incident_type_id: Number(incidentTypeId),
        //         incident_ids: selectedIncidents[incidentTypeId],
        //     })
        // );

        const incident_types = Object.keys(selectedIncidents)
            .filter(
                (incidentTypeId) =>
                    expandedRows[incidentTypeId] &&
                    selectedIncidents[incidentTypeId]?.length > 0
            )
            .map((incidentTypeId) => ({
                incident_type_id: Number(incidentTypeId),
                incident_ids: selectedIncidents[incidentTypeId],
            }));


        // 🚨 FINAL GUARD — THIS WAS MISSING
        if (incident_types.length === 0) {
            toast.error("At least one quest type must be selected.");
            return;
        }

        const payload = {
            course_id: courseId,
            incident_types,
        };

        // console.log("FINAL PAYLOAD ✅", payload);

        dispatch(
            saveCourseFixtures({
                payload,
                navigate,
                setLoader,
            })
        ).then(() => {
            dispatch(getIncidentTypes({ courseId, navigate, setLoader }));
            dispatch(courseList({ navigate, pageCount: 1, setLoader, locationId }));
            onHide();
        });
    };


    // Reset on (re)open. Two cases:
    //  - Edit (any row has `selected: true` from API) → mirror what the
    //    backend returned: expand previously-saved rows and use their
    //    saved Prompt selections.
    //  - Add (no row is `selected`) → start with everything unchecked,
    //    but pre-fill the first Prompt for every Quest Type so it
    //    visually appears as the default once the user enables the row.
    useEffect(() => {
        if (!show) return;

        setChosenTab(null);

        const list = courseIncidentTypesData || [];
        const isEdit = list.some((it) => it.selected);

        const newExpanded = {};
        const newSelectedIncidents = {};
        const newSelectedFixtures = {};

        list.forEach((incidentType) => {
            if (isEdit) {
                if (incidentType.selected) {
                    newExpanded[incidentType.id] = true;
                }
                if (incidentType.selected_incident_ids?.length) {
                    newSelectedIncidents[incidentType.id] =
                        incidentType.selected_incident_ids;
                } else if (incidentType.incidents?.length) {
                    // Pre-fill first Prompt for every row so newly-added
                    // Quest Types in edit mode also show the default
                    // Prompt as checked.
                    newSelectedIncidents[incidentType.id] = [
                        incidentType.incidents[0].id,
                    ];
                }
                incidentType.fixtures?.forEach((f) => {
                    if (f.selected) {
                        if (!newSelectedFixtures[incidentType.id]) {
                            newSelectedFixtures[incidentType.id] = {};
                        }
                        newSelectedFixtures[incidentType.id][f.id] = true;
                    }
                });
            } else if (incidentType.incidents?.length) {
                newSelectedIncidents[incidentType.id] = [
                    incidentType.incidents[0].id,
                ];
            }
        });

        setExpandedRows(newExpanded);
        setSelectedIncidents(newSelectedIncidents);
        setSelectedFixtures(newSelectedFixtures);
    }, [show, courseIncidentTypesData]);


    return (
        <Modal show={show} onHide={onHide} backdrop="static" size="xl" centered dialogClassName="quest-modal-width">
            <Modal.Header closeButton>
                <Modal.Title>
                    {mode === "add" ? "Select a Quest Type and Relevant Prompts" : "Select a Quest Type and Relevant Prompts"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="fixture_modal_body">
                <form onSubmit={onSubmit}>
                    <div className="d-flex align-items-center justify-content-between pb-3">
                        <p className="zone_paragraph">
                            {/* Choosing multiple quests here will chain them as one longer experience in which the player will have to
                            complete all the badges. */}
                            {freeAccessUntil &&
                                `Your organisation's free access ends on ${formatApiDate(
                                    freeAccessUntil
                                )}.`}
                        </p>
                        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                            <QuestTypeCheckout onPurchased={handlePurchased} />
                            <button
                                type="submit"
                                className="submit_btn"
                            >
                                Save
                            </button>
                        </div>
                    </div>
                    {tabs.length > 0 && (
                        <ul className="bundle_tabs quest_modal_tabs">
                            {/* All only earns a tab when there is more than
                                one category to combine. */}
                            {(tabs.length > 1
                                ? [...tabs, { id: ALL_TAB, name: "All" }]
                                : tabs
                            ).map((tab) => {
                                const tabId = String(tab.id);
                                return (
                                    <li
                                        key={`questTab_${tabId}`}
                                        className={`bundle_tabs_item ${
                                            tabId === activeTab ? "activeTab" : ""
                                        }`}
                                        onClick={() => setActiveTab(tabId)}
                                    >
                                        {tab.name}
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <div className="org_table table-responsive fixTableResponsive pr-3 questTable">
                        <table className="table fixTable">
                            <thead className="questHead">
                                <tr>
                                    <th scope="col" className="zone_table_heading org_table_column">
                                        Price
                                    </th>
                                    <th scope="col" className="zone_table_heading org_table_column"></th>
                                    <th scope="col" className="zone_table_heading org_table_column">
                                        Quest Type
                                    </th>
                                    <th scope="col" className="zone_table_heading org_table_column">
                                        Points of Interest that must be found to complete Quest
                                    </th>
                                    <th scope="col" className="zone_table_heading org_table_column">
                                        Select up to 3 Prompts relevant to this Quest area
                                    </th>
                                    <th scope="col" className="zone_table_heading org_table_column">
                                        Renew
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="questBody">
                                {visibleQuestTypes.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="text-center">
                                            No quest types in this category.
                                        </td>
                                    </tr>
                                )}
                                {visibleQuestTypes.map((incidentType) => {
                                    const hasFixtures = (incidentType.fixtures?.length || 0) > 0;
                                    const hasIncidents = (incidentType.incidents?.length || 0) > 0;
                                    const isRowDisabled = !hasFixtures || !hasIncidents;
                                    const ownership = resolveOwnership(
                                        incidentType,
                                        marketplaceIndex.get(incidentType.id)
                                    );
                                    // Only lock a row when ownership is actually known.
                                    const needsPurchase = ownership
                                        ? !ownership.owned || ownership.isExpired
                                        : false;
                                    const alreadyChosen = !!expandedRows[incidentType.id];
                                    // Cells that are inactive are dimmed
                                    // individually, never the whole row: the
                                    // price and buy action must stay legible.
                                    // Applied to cell *contents*, never the
                                    // <td>: a translucent cell would let the
                                    // row background bleed through.
                                    const dim =
                                        isRowDisabled || (needsPurchase && !alreadyChosen)
                                            ? { opacity: 0.55 }
                                            : undefined;
                                    return (
                                    <React.Fragment key={incidentType.id}>
                                        <tr
                                            style={{
                                                pointerEvents: isRowDisabled ? "none" : "auto",
                                            }}
                                        >
                                            <td className="quest_price_cell">
                                                {ownership?.viaFreeAccess ? (
                                                    <>
                                                        Free
                                                        <div className="quest_action_cell_expiry">
                                                            {/* marketplace price if the incidents
                                                                endpoint did not send one */}
                                                            Normally{" "}
                                                            {questTypePrice({
                                                                price:
                                                                    ownership.price ??
                                                                    incidentType.price,
                                                            })}
                                                        </div>
                                                    </>
                                                ) : (
                                                    questTypePrice(incidentType)
                                                )}
                                            </td>
                                            <td>
                                                <label
                                                    className="check_box ml-2"
                                                    htmlFor={`expand-${incidentType.id}`}
                                                    style={{
                                                        cursor: isRowDisabled ? "not-allowed" : "pointer",
                                                        ...dim,
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        id={`expand-${incidentType.id}`}
                                                        checked={alreadyChosen}
                                                        disabled={
                                                            isRowDisabled ||
                                                            (needsPurchase && !alreadyChosen)
                                                        }
                                                        onChange={() => {
                                                            if (isRowDisabled) return;
                                                            // Already-chosen rows stay unselectable but
                                                            // can still be removed from the quest.
                                                            if (needsPurchase && !alreadyChosen) return;
                                                            handleExpandToggle(incidentType.id);
                                                        }}
                                                    />

                                                    <span
                                                        className="checkbox_check_course"
                                                        style={{
                                                            backgroundColor: expandedRows[incidentType.id]
                                                                ? `#${incidentType.color_type}`
                                                                : "",
                                                            border: expandedRows[incidentType.id]
                                                                ? `2px solid #${incidentType.color_type}`
                                                                : "",
                                                        }}
                                                    ></span>
                                                </label>
                                            </td>
                                            <td className="incident-type-cell">
                                                <div className="incident-type-content" style={dim}>
                                                    {incidentType.image && (
                                                        <img
                                                            src={`${process.env.REACT_APP_MEDIA_URL_ORG}${incidentType.image}`}
                                                            alt={incidentType.name}
                                                            className="incident-type-img"
                                                        />
                                                    )}
                                                    <span className="incident-type-name">{incidentType.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", ...dim }}>
                                                    {incidentType.fixtures?.map((fixture) => (
                                                        <img
                                                            key={fixture.id}
                                                            src={`${process.env.REACT_APP_MEDIA_URL_ORG}${fixture.image}`}
                                                            alt={fixture.name}
                                                            title={fixture.name}
                                                            style={{
                                                                width: "50px",
                                                                height: "50px",
                                                                objectFit: "contain",
                                                            }}
                                                        />
                                                    ))}
                                                </div>
                                            </td>
                                            <td style={{ padding: '12px 15px', verticalAlign: 'top' }}>
                                                <div style={{ display: 'flex', gap: '20px', ...dim }}>
                                                    <div
                                                        style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(2,1fr)',
                                                            background: '#B4D5CA',
                                                            borderRadius: '6px'
                                                        }}
                                                    >
                                                        {incidentType.incidents.map((incident) => (
                                                            <div
                                                                key={incident.id}
                                                                style={{ padding: '8px', display: "flex" }}
                                                            >
                                                                <label
                                                                    className="check_box"
                                                                    htmlFor={`incident-${incidentType.id}-${incident.id}`} // ✅ fixed ID
                                                                    style={{ marginRight: "8px" }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`incident-${incidentType.id}-${incident.id}`} // ✅ fixed ID
                                                                        checked={
                                                                            selectedIncidents[incidentType.id]?.includes(incident.id) || false
                                                                        }
                                                                        disabled={
                                                                            (selectedIncidents[incidentType.id]?.length || 0) >= 3 &&
                                                                            !selectedIncidents[incidentType.id]?.includes(incident.id)
                                                                        }
                                                                        onChange={() =>
                                                                            handleIncidentToggle(incidentType.id, incident.id)
                                                                        }
                                                                    />

                                                                    <span
                                                                        className="checkbox_check_course"
                                                                        style={{
                                                                            backgroundColor: selectedIncidents[incidentType.id]?.includes(incident.id)
                                                                                ? `#${incidentType.color_type}`
                                                                                : "",
                                                                            border: selectedIncidents[incidentType.id]?.includes(incident.id)
                                                                                ? `2px solid #${incidentType.color_type}`
                                                                                : "",
                                                                        }}
                                                                    ></span>
                                                                </label>

                                                                <div className="incident-image">
                                                                    <div className="incident-image-portal">
                                                                        <div className="incident-image-portal-text">
                                                                            <div
                                                                                className="incident-image-portal-text-name incident-type-name"
                                                                                title={incident.name}
                                                                            >
                                                                                {incident.name?.length > 15
                                                                                    ? `${incident.name.substring(0, 15)}...`
                                                                                    : incident.name}
                                                                            </div>
                                                                            <p className="incident-image-portal-text-span incident-type-name">
                                                                                {incidentType.name}
                                                                            </p>
                                                                        </div>
                                                                    </div>

                                                                    <img
                                                                        src={`${process.env.REACT_APP_MEDIA_URL_ORG}${incident.image}`}
                                                                        alt={incident.name}
                                                                        className="exact-incident-img"
                                                                    />
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </td>
                                            {/* Always interactive: a quest type you do not own
                                                arrives with no fixtures or prompts, which disables
                                                the row, and the buy action must still be clickable. */}
                                            <td
                                                className="quest_action_cell"
                                                style={{ pointerEvents: "auto" }}
                                            >
                                                {renderPurchaseAction(incidentType, ownership)}
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </form>
            </Modal.Body>
        </Modal >
    );
};

export default QuestModal;
